/** Pure helpers shared by the runtime loader and the i18n scripts. */

export type Catalogue = Record<string, unknown>

/** The namespace a catalogue folder owns (I18N.md rule 7): `job-offers` → `jobOffers`, `_example` → `example`. */
export function namespaceOf(source: string): string {
  if (source === 'shared') return 'common'
  return source.replace(/^_+/, '').replace(/[-_]+([a-z0-9])/g, (_, char: string) => char.toUpperCase())
}

/**
 * `/src/shared/i18n/fr.json` → `{ source: 'shared', locale: 'fr' }`;
 * `/src/modules/job-offers/i18n/en.json` → `{ source: 'job-offers', locale: 'en' }`; anything else (e.g.
 * `legacy-map.json`) → `null`.
 */
export function catalogueFile(file: string): { source: string; locale: string } | null {
  const match = /\/(?:shared|modules\/([^/]+))\/i18n\/([^/]+)\.json$/.exec(file)
  if (!match?.[2] || match[2] === 'legacy-map') return null
  return { source: match[1] ?? 'shared', locale: match[2] }
}

function isPlainObject(value: unknown): value is Catalogue {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Deep-merges catalogues (each holds its namespace as its single top-level key). */
export function mergeCatalogues(catalogues: Catalogue[]): Catalogue {
  const merge = (target: Catalogue, source: Catalogue): Catalogue => {
    for (const [key, value] of Object.entries(source)) {
      const existing = target[key]
      target[key] = isPlainObject(existing) && isPlainObject(value) ? merge({ ...existing }, value) : value
    }
    return target
  }
  return catalogues.reduce<Catalogue>((merged, catalogue) => merge(merged, catalogue), {})
}
